/**
 * Currency rates + price formatting.
 *
 * Source of truth: WP admin at cms.bpderma.com (Settings → Meier).
 * Endpoint: GET /wp-json/meier/v1/settings → { eur_try_rate, eur_usd_rate }
 *
 * Woo product prices are stored as EUR amounts. TRY/USD are derived using
 * the rates above and the rounding rule defined alongside them in WP:
 *   round(eur * rate / 5) * 5 - 0.05
 * (snap to nearest .95)
 */

import { unstable_cache } from "next/cache";

export type Currency = "EUR" | "USD" | "TRY";

export interface Rates {
  eurToTry: number;
  eurToUsd: number;
}

export const DEFAULT_RATES: Rates = { eurToTry: 37.5, eurToUsd: 1.17 };

const RATES_CACHE_TAG = "fx-rates";
const RATES_REVALIDATE_SECONDS = 3600; // matches WP widget copy: "updates within 1 hour"

const fetchRatesCached = unstable_cache(
  async (): Promise<Rates> => {
    const base = process.env.CMS_WP_URL?.replace(/\/+$/, "");
    if (!base) return DEFAULT_RATES;
    try {
      const res = await fetch(`${base}/wp-json/meier/v1/settings`, {
        headers: { Accept: "application/json" },
        next: { revalidate: RATES_REVALIDATE_SECONDS, tags: [RATES_CACHE_TAG] },
      });
      if (!res.ok) return DEFAULT_RATES;
      const data = (await res.json()) as { eur_try_rate?: number; eur_usd_rate?: number };
      const eurToTry = Number(data.eur_try_rate);
      const eurToUsd = Number(data.eur_usd_rate);
      return {
        eurToTry: Number.isFinite(eurToTry) && eurToTry > 0 ? eurToTry : DEFAULT_RATES.eurToTry,
        eurToUsd: Number.isFinite(eurToUsd) && eurToUsd > 0 ? eurToUsd : DEFAULT_RATES.eurToUsd,
      };
    } catch {
      return DEFAULT_RATES;
    }
  },
  ["fx:rates"],
  { tags: [RATES_CACHE_TAG], revalidate: RATES_REVALIDATE_SECONDS }
);

export async function getRates(): Promise<Rates> {
  return fetchRatesCached();
}

/** Snap to nearest .95 — used for EUR & USD (consumer-friendly Western pricing). */
function snap95(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value / 5) * 5 - 0.05;
}

/** Snap to nearest 50, ending .00 — used for TRY (cleaner round numbers). */
function snap50(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value / 50) * 50;
}

/**
 * Convert an EUR amount into the chosen display currency.
 * Returns the converted *number* (not formatted). 0 if eur is missing.
 */
export function convertFromEur(eur: number | undefined, currency: Currency, rates: Rates): number {
  if (!eur || !Number.isFinite(eur) || eur <= 0) return 0;
  if (currency === "EUR") return Math.round(eur * 100) / 100;
  if (currency === "TRY") return snap50(eur * rates.eurToTry);
  if (currency === "USD") return snap95(eur * rates.eurToUsd);
  return 0;
}

const CURRENCY_LOCALES: Record<Currency, string> = {
  EUR: "de-DE",
  USD: "en-US",
  TRY: "tr-TR",
};

const CURRENCY_PREFIX: Record<Currency, string> = {
  EUR: "€",
  USD: "$",
  TRY: "",
};

const CURRENCY_SUFFIX: Record<Currency, string> = {
  EUR: "",
  USD: "",
  TRY: " ₺",
};

/**
 * Format an EUR amount into the chosen display currency, locale-aware.
 *  EUR  → "€16.90"
 *  USD  → "$19.95"
 *  TRY  → "634,95 ₺"
 */
export function formatPrice(eur: number | undefined, currency: Currency, rates: Rates): string {
  const value = convertFromEur(eur, currency, rates);
  if (value <= 0) return "";
  const formatted = value.toLocaleString(CURRENCY_LOCALES[currency], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_PREFIX[currency]}${formatted}${CURRENCY_SUFFIX[currency]}`;
}

/** Lowercase ISO code Stripe expects. */
export function stripeCurrencyCode(currency: Currency): string {
  return currency.toLowerCase();
}
